import type { ProviderDto, ProviderType, ProviderTypeInfo } from '@agent-platform/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/error-alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api, ApiError } from '@/lib/api';

export interface CatalogEntry extends ProviderTypeInfo {
  type: ProviderType;
}

interface Props {
  tenantId: string;
  catalog: CatalogEntry[];
  claudeHostLoginEnabled: boolean;
  /** Present when editing. */
  provider?: ProviderDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProviderDialog({
  tenantId,
  catalog,
  claudeHostLoginEnabled,
  provider,
  open,
  onOpenChange,
}: Props) {
  const queryClient = useQueryClient();
  const editing = Boolean(provider);
  const [type, setType] = useState<ProviderType>(provider?.type ?? 'anthropic');
  const [name, setName] = useState(provider?.name ?? '');
  const [baseUrl, setBaseUrl] = useState(provider?.baseUrl ?? '');
  const [apiKey, setApiKey] = useState('');
  const [removeKey, setRemoveKey] = useState(false);
  const [defaultModel, setDefaultModel] = useState(provider?.defaultModel ?? '');
  const info = catalog.find((c) => c.type === type);

  const save = useMutation({
    mutationFn: () => {
      if (!editing) {
        return api<ProviderDto>('/providers', {
          method: 'POST',
          body: {
            name,
            type,
            ...(baseUrl && info?.baseUrl !== 'none' ? { baseUrl } : {}),
            ...(apiKey ? { apiKey } : {}),
            ...(defaultModel ? { defaultModel } : {}),
          },
        });
      }
      const body: Record<string, unknown> = { name, defaultModel: defaultModel || null };
      if (info?.baseUrl !== 'none') body.baseUrl = baseUrl || null;
      if (apiKey) body.apiKey = apiKey;
      else if (removeKey) body.apiKey = null;
      return api<ProviderDto>(`/providers/${provider!.id}`, { method: 'PATCH', body });
    },
    onSuccess: (saved) => {
      toast.success(
        editing ? `${saved.name} updated` : `${saved.name} added — run a test to verify it`,
      );
      void queryClient.invalidateQueries({ queryKey: ['providers', tenantId] });
      onOpenChange(false);
    },
  });

  const fieldErr = (path: string) =>
    save.error instanceof ApiError ? save.error.fieldError(path) : undefined;
  const keyOptional =
    info?.apiKey === 'optional' && (type !== 'claude_agent_sdk' || claudeHostLoginEnabled);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${provider!.name}` : 'Add a model provider'}</DialogTitle>
          <DialogDescription>{info?.description}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <ErrorAlert error={save.error} />
          <div className="grid gap-2">
            <Label htmlFor="provider-type">Provider</Label>
            <Select
              value={type}
              onValueChange={(v) => {
                const next = v as ProviderType;
                setType(next);
                // A hidden field must not keep (and submit) a stale or autofilled value.
                if (catalog.find((c) => c.type === next)?.baseUrl === 'none') setBaseUrl('');
              }}
              disabled={editing}
            >
              <SelectTrigger id="provider-type" aria-label="Provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {catalog.map((c) => (
                  <SelectItem key={c.type} value={c.type}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="provider-name">Name</Label>
            <Input
              id="provider-name"
              autoComplete="off"
              data-1p-ignore
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {fieldErr('name') && <p className="text-destructive text-sm">{fieldErr('name')}</p>}
          </div>
          {info && info.baseUrl !== 'none' && (
            <div className="grid gap-2">
              <Label htmlFor="provider-base-url">
                Base URL{info.baseUrl === 'optional' ? ' (optional)' : ''}
              </Label>
              <Input
                id="provider-base-url"
                inputMode="url"
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                placeholder={info.baseUrlPlaceholder ?? ''}
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
              />
              {fieldErr('baseUrl') && (
                <p className="text-destructive text-sm">{fieldErr('baseUrl')}</p>
              )}
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="provider-api-key">API key{keyOptional ? ' (optional)' : ''}</Label>
            <Input
              id="provider-api-key"
              type="password"
              // "new-password" stops browsers filling a saved login password into an API key field.
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              placeholder={
                editing && provider!.apiKey
                  ? `Saved key ending in ${provider!.apiKey.last4} — type to replace`
                  : (info?.apiKeyPlaceholder ?? '')
              }
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                setRemoveKey(false);
              }}
            />
            {fieldErr('apiKey') && <p className="text-destructive text-sm">{fieldErr('apiKey')}</p>}
            {!fieldErr('apiKey') &&
              editing &&
              provider!.apiKey &&
              !apiKey &&
              !removeKey &&
              baseUrl !== (provider!.baseUrl ?? '') && (
                <p className="text-muted-foreground text-xs">
                  Re-enter the API key to use it with the new base URL. Saved keys are never sent to
                  a changed endpoint.
                </p>
              )}
            {editing && provider!.apiKey && keyOptional && (
              <label className="text-muted-foreground flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={removeKey}
                  onChange={(e) => setRemoveKey(e.target.checked)}
                  disabled={Boolean(apiKey)}
                />
                Remove the saved key
              </label>
            )}
            {type === 'claude_agent_sdk' && !apiKey && claudeHostLoginEnabled && (
              <p className="text-muted-foreground text-xs">
                Without a key, agents use the Claude Code login of the server.
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="provider-default-model">Default model (optional)</Label>
            {provider && provider.models.length > 0 ? (
              <Select
                value={defaultModel || '__none'}
                onValueChange={(v) => setDefaultModel(v === '__none' ? '' : v)}
              >
                <SelectTrigger id="provider-default-model" aria-label="Default model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">None</SelectItem>
                  {provider.models.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="provider-default-model"
                value={defaultModel}
                onChange={(e) => setDefaultModel(e.target.value)}
              />
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={save.isPending || !name}>
              {save.isPending ? 'Saving…' : 'Save provider'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
